/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { enrichResultTitle } from '@atlassian/search-common/enriched-title';
import { useKeyboardNavigation } from '@atlassian/search-dialog/search-keyboard';

// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useApplicationMode, useFeatures } from '../../../controllers/store/bootstrap';
import {
	onHighlightedWithKeyboard,
	onKeyboardPress,
} from '../../utils/events/dropdown-keyboard-interacted';
import { Link } from '../link-component';

import { messages } from './messages';
import { type SearchLinkProps } from './types';

const searchLinkStyles = xcss({
	width: 'fill',
	padding: 'space.100',
	display: 'grid',
	columnGap: 'space.100',
	gridTemplateColumns: 'auto 1fr auto',
	gridTemplateAreas: `
    "icon title subheading"
  `,
	alignItems: 'center',
	height: '40px',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	borderRadius: 'radius.large',
	cursor: 'pointer',
});

const searchLinkWithDescriptionStyles = xcss({
	width: 'fill',
	padding: 'space.100',
	display: 'grid',
	columnGap: 'space.100',
	gridTemplateColumns: 'auto 1fr',
	gridTemplateAreas: `
    "icon title subheading"
    "icon description subheading"
  `,
	alignItems: 'center',
	height: '50px',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	borderRadius: 'radius.large',
	cursor: 'pointer',
});

const iconStyles = xcss({
	gridArea: 'icon',
});

const titleStyles = xcss({
	gridArea: 'title',
	color: 'color.text',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	font: token('font.body'),
});

const descriptionStyles = xcss({
	gridArea: 'description',
	display: 'flex',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: token('font.body.small'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 'inherit',
	color: 'color.text.subtle',
	paddingTop: 'space.075',
});

const subheadingStyles = xcss({
	gridArea: 'subheading',
	display: 'flex',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: token('font.body.small'),
	color: 'color.text.subtle',
	alignItems: 'center',
	gap: 'space.050',
});

const DropdownResultContent = ({
	title,
	description,
	subheading,
	icon,
	onClick,
	highlightedTitle,
	rightPanel,
	linkRef,
}: SearchLinkProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const applicationMode = useApplicationMode();
	const { formatMessage } = useIntl();

	const onSubmit = (e: KeyboardEvent, target: HTMLElement) => {
		if (e.key === 'Enter') {
			createAnalyticsEvent(onKeyboardPress(e.key, title, applicationMode)).fire();

			onClick({ target });
			if (linkRef?.current) {
				linkRef.current.click();
			}
		}
	};

	const onHighlighted = (index: number) => {
		createAnalyticsEvent(onHighlightedWithKeyboard(index, applicationMode)).fire();
	};

	const [isKeyboardHighlighted, setRef] = useKeyboardNavigation<HTMLDivElement>({
		onKeydownCallback: onSubmit,
		onHighlightedCallback: onHighlighted,
	});

	const [isMouseHighlighted, setIsMouseHighlighted] = useState(false);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			ref={setRef}
			xcss={description ? searchLinkWithDescriptionStyles : searchLinkStyles}
			backgroundColor={
				isKeyboardHighlighted
					? 'color.background.neutral.subtle.hovered'
					: 'color.background.neutral.subtle'
			}
			onClick={onClick}
			onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.preventDefault()}
			aria-label={formatMessage(messages.searchLinkLabel)}
			onMouseOver={() => setIsMouseHighlighted(true)}
			onMouseLeave={() => setIsMouseHighlighted(false)}
			testId="search-page-dropdown-result"
		>
			<Box xcss={iconStyles}>{icon}</Box>
			<Box xcss={titleStyles}>{highlightedTitle ? enrichResultTitle(highlightedTitle) : title}</Box>
			{subheading && <Box xcss={subheadingStyles}>{subheading}</Box>}
			{description && <Box xcss={descriptionStyles}>{description}</Box>}
			{rightPanel && (isKeyboardHighlighted || isMouseHighlighted) && rightPanel}
		</Box>
	);
};

export const DropdownResultLink: (props: SearchLinkProps) => JSX.Element = (
	props: SearchLinkProps,
) => {
	const linkRef = useRef<HTMLAnchorElement>(null);

	return (
		<Link
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{ textDecoration: 'none', color: 'inherit' }}
			ref={linkRef}
			href={props.navigationUrl || ''}
			testId="search-page-dropdown-result-link"
		>
			<DropdownResultContent {...props} linkRef={linkRef} />
		</Link>
	);
};

export const DropdownResultChromeExtension: (props: SearchLinkProps) => JSX.Element = (
	props: SearchLinkProps,
) => {
	const [features] = useFeatures();

	const onClick = useCallback(() => {
		if (!features?.chromeExtension?.enabled) {
			return;
		}

		const { onInputDropdownResultSelect } = features.chromeExtension;
		onInputDropdownResultSelect({ title: props.title, url: props.navigationUrl });
	}, [features?.chromeExtension, props.title, props.navigationUrl]);

	return <DropdownResultContent {...props} onClick={onClick} />;
};

export const SearchLink: (props: SearchLinkProps) => JSX.Element = (props: SearchLinkProps) => {
	const [features] = useFeatures();

	if (features?.chromeExtension?.enabled) {
		return <DropdownResultChromeExtension {...props} />;
	} else if (props.navigationUrl) {
		return <DropdownResultLink {...props} />;
	} else {
		return <DropdownResultContent {...props} />;
	}
};
